import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Download, Copy, QrCode } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";

export function QrCodeDialog({ url, name, trigger, size = 240 }) {
  const { t } = useLang();
  const canvasWrapRef = useRef(null);

  const download = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${(name || "qr-code").replace(/[^a-z0-9-_]+/gi, "_")}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("toast_link_copied"));
    } catch {
      toast.error("Copy failed");
    }
  };

  // QR embeds the URL with ?src=qr so the credit system tracks correctly.
  const qrUrl = url.includes("?") ? `${url}&src=qr` : `${url}?src=qr`;

  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-sm" data-testid="qr-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-[#E57C4F]" /> {t("qr_dialog_title")}
          </DialogTitle>
          <DialogDescription>{t("qr_dialog_desc")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-2">
          <div ref={canvasWrapRef} className="p-4 bg-white rounded-xl border border-slate-200" data-testid="qr-canvas-wrap">
            <QRCodeCanvas
              value={qrUrl}
              size={size}
              level="H"
              includeMargin={false}
              fgColor="#0A2540"
            />
          </div>
          {name && <div className="text-sm font-medium text-slate-700 truncate max-w-full px-4">{name}</div>}
          <div className="w-full text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5 truncate" title={qrUrl}>
            {qrUrl}
          </div>
          <div className="flex gap-2 w-full">
            <Button onClick={download} className="flex-1 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="qr-download-btn">
              <Download className="w-4 h-4 mr-1.5" /> {t("qr_download_btn")}
            </Button>
            <Button variant="outline" onClick={copyLink} className="flex-1" data-testid="qr-copy-link-btn">
              <Copy className="w-4 h-4 mr-1.5" /> {t("qr_copy_link")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
